/* eslint-disable react-hooks/exhaustive-deps */
/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoginMutation, useRefreshMutation } from "../../store/api/auth/authApiSlice";
import { setCredentials } from "../../store/api/auth/reducer";
import { IRetrievedTokenData } from "../../store/api/auth/types";
import { LOCAL_STORAGE, ROUTES } from "../../globals/globals";
import { setCookie } from "../../utils/cookie";
import { useStoreDispatch } from "../../store/hooks";
import { setOpenToast } from "../../store/helpers/helperReducers";
import { errorMessage, isJwtExpired } from "../../utils/helpers";
import { IKeyValue } from "../Administrations/types";
import { useGetAccessControlSettingsQuery, useGetPasswordSettingsQuery, useGetStatusQuery } from "../../store/api/admin/adminApiSlice";
import { setAccessControlSettings, setPasswordSettings, setShowBanner } from "../../store/api/admin/reducers";
import { convertPasswordPropertiesToSettings, convertSystemPropertiesToSettings } from "../Administrations/helpers";

export const useAuthForm = () => {
  const errorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useStoreDispatch();

  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errMsg, setErrMsg] = useState<IKeyValue>({});
  const isSsoEnabled = window.SSO_ENABLED === "true";
  const [hasCheckedSso, setHasCheckedSso] = useState(false);
  const [shouldRedirectToSso, setShouldRedirectToSso] = useState<boolean | null>(null);
  const [shouldHoldLoginUi, setShouldHoldLoginUi] = useState(false);
  const [login, { isLoading }] = useLoginMutation();
  const [refresh, { isLoading: isRefreshingToken }] = useRefreshMutation();

  const [shouldFetch, setShouldFetch] = useState(false);

  const { data: statusData, isLoading: isLoadingStatus, isError: isStatusError, error: statusError } = useGetStatusQuery(undefined, {
    skip: !shouldFetch,
  });

  const fetchStatus = useCallback(() => {
    setShouldFetch(true);
  }, []);

  const { data: getAccessControlSettings } = useGetAccessControlSettingsQuery();
  const { data: passwordSettings } = useGetPasswordSettingsQuery();
  const switchAuthModeHandler = useCallback(() => {
    setIsLogin((prevState) => !prevState);
  }, []);

  useEffect(() => {
    fetchAccessControlSettings();
  }, [getAccessControlSettings]);

  useEffect(() => {
    fetchPasswordSettings();
  }, [passwordSettings]);


  const fetchAccessControlSettings = () => {
    try {
      const response: any = getAccessControlSettings;

      if (response.error)
        throw response.error;

      const settings = convertSystemPropertiesToSettings(response);
      setAccessControlSettings(settings);
      dispatch(setAccessControlSettings(settings));
    } catch (error: any) {
      console.log(errorMessage(error));
    };
  }

  const fetchPasswordSettings = () => {
    try {
      const response: any = passwordSettings;

      if (response.error)
        throw response.error;

      const settings = convertPasswordPropertiesToSettings(response);
      setPasswordSettings(settings);
      dispatch(setPasswordSettings(settings));
    } catch (error: any) {
      console.log(errorMessage(error));
    };
  }

  const checkSsoEnabled = useCallback(async () => {
    setHasCheckedSso(true);
    return isSsoEnabled;
  }, [isSsoEnabled]);

  const usernameInputHandler = (e: React.FormEvent<HTMLInputElement>) => setUsername(e.currentTarget.value);
  const passwordInputHandler = (e: React.FormEvent<HTMLInputElement>) => setPassword(e.currentTarget.value);

  const loginHandler = async (event: React.FormEvent | null) => {
    event?.preventDefault();

    try {
      const tokenData: IRetrievedTokenData = await login({ username, password }).unwrap();
      console.log(`User logged in: ${tokenData.idToken.slice(0, 9)}`);
      dispatch(setCredentials({ ...tokenData }));

      dispatch(setShowBanner(true));

      setUsername("");
      setPassword("");

      navigate(ROUTES.NODES.Path);

    } catch (err: any) {
      if (err?.status === 400) {
        setErrMsg({ invalid: "Invalid Username or Password" });
      } else if (err?.status === 401) {
        setErrMsg({ invalid: "Login failed" });
      } else if (err?.status === 403) {
        setErrMsg({ invalid: "Account locked. Please contact your system administrator." });
      } else if (err?.status === 423) {
        localStorage.setItem(LOCAL_STORAGE.USER, username);
        setErrMsg({ "expired": "Password Expired. " });
      } else {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(err) }));
      }

      errorRef?.current?.focus();
    }
  };

  const [searchParams] = useSearchParams();
  const jwt = searchParams.get("token");
  const ssoLoginUrl = "/v1/login/saml2/sso";
  const isLoginPath = typeof window !== "undefined" ? window.location.pathname === "/login" : false;
  const redirectToSso = window.NGINX_SERVER_PORT === "3000";
  const storedToken = localStorage.getItem(LOCAL_STORAGE.TOKEN);
  const hasActiveLocalSession = !!storedToken && !isJwtExpired(storedToken);
  const isStandbyOrFailoverServer = ["HA_SR", "HA_SY", "HA_F"].includes(statusData?.ha_state ?? "");

  useEffect(() => {
    const initializeAuthState = async () => {
      const isEnabled = await checkSsoEnabled();
      fetchStatus();

      const redirect = isLoginPath && isEnabled && !isStandbyOrFailoverServer && redirectToSso && !jwt && !hasActiveLocalSession;
      setShouldRedirectToSso(redirect);
      setShouldHoldLoginUi(!redirect);
    };

    initializeAuthState();
  }, [checkSsoEnabled, fetchStatus, hasActiveLocalSession, isLoginPath, isStandbyOrFailoverServer, jwt, redirectToSso]);

  useEffect(() => {
    if (shouldRedirectToSso === null) {
      return;
    }

    const redirect = hasCheckedSso && isLoginPath && isSsoEnabled && !isStandbyOrFailoverServer && redirectToSso && !jwt && !hasActiveLocalSession;
    const holdLoginUi = !hasCheckedSso || !!jwt || isRefreshingToken || (isSsoEnabled && isLoadingStatus) || !redirect;

    setShouldRedirectToSso(redirect);
    setShouldHoldLoginUi(holdLoginUi);
  }, [hasCheckedSso, isLoginPath, isLoadingStatus, isSsoEnabled, isStandbyOrFailoverServer, jwt, hasActiveLocalSession, redirectToSso, isRefreshingToken, shouldRedirectToSso]);

  const refreshHandler = useCallback(
    async (token: string) => {
      try {
        const tokenData: IRetrievedTokenData = await refresh(token).unwrap();
        console.log(`User logged in: ${tokenData.idToken.slice(0, 9)}`);

        dispatch(setCredentials({ ...tokenData }));
        navigate(ROUTES.NODES.Path);
      } catch (err: any) {
        if (err?.status === 400) {
          setErrMsg({ invalid: "Invalid Username or Password" });
        } else if (err?.status === 401) {
          setErrMsg({ invalid: "Login failed" });
        } else if (err?.status === 403) {
          setErrMsg({ invalid: "Account locked. Please contact your system administrator." });
        } else if (err?.status === 423) {
          setErrMsg({ "expired": "Password Expired. Please reset your password" });
        } else {
          dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(err) }));
        }

        errorRef?.current?.focus();
      }
    },
    [dispatch, navigate, refresh]
  );

  useEffect(() => {
    if (jwt) {
      localStorage.setItem(LOCAL_STORAGE.REFRESH_TOKEN, jwt);
      localStorage.setItem(LOCAL_STORAGE.TOKEN, jwt);
      document.cookie = LOCAL_STORAGE.JWTCOOKIE + '=; path=/ssh; expires=Thu, 01 Jan 1970 00:00:00 GMT;';
      setCookie(LOCAL_STORAGE.JWTCOOKIE, jwt);
      document.cookie = LOCAL_STORAGE.JWTCOOKIE + '=' + jwt + '; path=/ssh;';
      refreshHandler(jwt);
    }
  }, [jwt, refreshHandler]);

  useEffect(() => {
    if (!shouldRedirectToSso) return;
    window.location.replace(ssoLoginUrl);
  }, [shouldRedirectToSso]);

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;
    if (name === "username") {
      !!!value && setErrMsg({ ...errMsg, [name]: `User Name is required!` });
    }
    if (name === "password") {
      !!!value && setErrMsg({ ...errMsg, [name]: `Password is required!` });
    }
  };

  return {
    isLogin,
    submitHandler: loginHandler,
    username,
    password,
    usernameInputHandler,
    passwordInputHandler,
    setUsername,
    setPassword,
    errMsg,
    setErrMsg,
    isLoading,
    switchAuthModeHandler,
    onBlurHandler,
    isSsoEnabled,
    shouldRedirectToSso,
    shouldHoldLoginUi,
    statusData,
    statusError,
    navigate
  };
};